import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import moment from "moment-timezone";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Modal } from "../../shared/components/modal/Modal";

// This is for scheduling the site survey...

const SiteSurvey = ({
  projectID,
  contractSigned,
  customerAddress,
  primaryContact,
  callComplete,
  setCallComplete,
  envelopeStatus,
}) => {
  const envelopeCompleted =
    envelopeStatus === "completed" || envelopeStatus === "document.completed";
  const [territory, setTerritory] = useState("");
  const [timezone, setTimezone] = useState("America/Denver");
  const [formatStartTime, setFormatStartTime] = useState("");
  const [formatEndTime, setFormatEndTime] = useState("");
  const initialAppointment = {
    contactId: "",
    resourceId: "",
    territoryId: "",
    day: "",
    dayOfWeek: "",
    startTime: "",
    endTime: "",
    startTimeDatetime: "",
    endTimeDatetime: "",
  };
  const [currentAppt, setCurrentAppt] = useState("");
  const [appointment, setAppointment] = useState(initialAppointment);
  const [timeSlots, setTimeSlots] = useState([]);
  const [confirmModal, setConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  // Environment detection for API URL
  const isStaging =
    window.location.hostname.includes("staging") ||
    process.env.NODE_ENV === "development";

  const surveyTimesApiUrl = isStaging
    ? "https://proposalist-api-solar-d828cd2f990a.herokuapp.com/proposalist_php_api/site_survey"
    : "https://proposalist-api-solar-prod-041c14b8b909.herokuapp.com/proposalist_php_api/site_survey/";
  const surveyApiUrl = `https://dev.iondeveloper.com/api/proposal/site-survey/?api_key=${process.env.REACT_APP_API_KEY}`;

  const headers = {
    "Content-Type": "application/json",
    "X-API-Key": process.env.REACT_APP_API_KEY,
  };

  const cancelAppointment = () => {
    setAppointment(initialAppointment);
    setFormatStartTime("");
    setFormatEndTime("");
  };

  // Helper function to convert time slot to UTC in territory timezone
  const convertTimeSlotToUTC = (date, time, targetTimezone) => {
    const dateTimeString = `${date}, ${time}`;
    return moment
      .tz(dateTimeString, "M/D/YY, h:mm A", targetTimezone)
      .utc()
      .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
  };

  // Parse the strings into Moment.js objects with the specified timeZone
  const startTime =
    appointment.day && appointment.startTime
      ? moment.tz(
          `${appointment.day}, ${appointment.startTime}`,
          "M/D/YY, h:mm A",
          territory?.time_zone || territory?.timeZone,
        )
      : null;
  const endTime =
    appointment.day && appointment.endTime
      ? moment.tz(
          `${appointment.day}, ${appointment.endTime}`,
          "M/D/YY, h:mm A",
          territory?.time_zone || territory?.timeZone,
        )
      : null;

  // const timezone = territory?.time_zone || territory?.timeZone;

  // Convert to UTC
  const startTimeUTC = startTime?.utc();
  const endTimeUTC = endTime?.utc();

  const getCustomerAppt = async () => {
    try {
      const res = await axios.post(surveyApiUrl, {
        method: "get_customer_appt",
        project_id: projectID,
      });
      // console.log(res.data.data)
      if (res.data.data.id) {
        const { id, startTime, timeZone } = res.data.data;
        // console.log(res.data)
        setCurrentAppt({
          id: id,
          startTime: moment(startTime)
            .tz(timeZone)
            .format("dddd, M/D/YY, h:mm A"),
        });
      }
    } catch (error) {
      console.error("Error fetching survey data:", error);
    }
  };

  const getCallPilot = async () => {
    try {
      const res = await axios.post(surveyApiUrl, {
        method: "get_call_pilot",
        project_id: projectID,
      });
      setCallComplete(res.data.data.callPilotDate);
    } catch (error) {
      console.error("Error fetching survey data:", error);
    }
  };

  const createAppt = async () => {
    setLoading(true);
    try {
      const res = await axios.post(surveyApiUrl, {
        method: "create_appt",
        projectId: projectID,
        startTime: formatStartTime,
        endTime: formatEndTime,
        contactId: appointment.contactId,
        resourceId: appointment.resourceId,
        // contactId: '0058b00000GHy2bAAD', Austin's ID's for testing
        // resourceId: '0Hn8b000000gQQZCA2',
        territoryId: appointment.territoryId || territory.territoryId,
      });
      setLoading(false);
      getCustomerAppt();
      // Close the modal on successful appointment creation
      setConfirmModal(false);
      setAppointment(initialAppointment);
      // console.log(res.data)
    } catch (error) {
      console.error("Error fetching survey data:", error);
      setLoading(false);
    }
  };

  const getSurveyTimes = async (projectId, customerAddress, headers) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${surveyTimesApiUrl}?api_key=${process.env.REACT_APP_API_KEY}&project_id=${projectId}&customer_address=${customerAddress}`,
        { headers },
      );

      const allSlots = (res.data?.time_slots || []).map((slot) => ({
        date: slot.day,
        dayOfWeek: slot.day_of_week,
        startTime: slot.start_time,
        endTime: slot.end_time,
        startTimeDatetime: slot.start_time_datetime,
        endTimeDatetime: slot.end_time_datetime,
        resourceId: slot.resource_id,
        contactId: slot.contact_id,
        territoryId: slot.territory_id,
        territoryTimeZone: slot.territory_time_zone,
      }));

      if (!isMountedRef.current) return;

      const firstSlot = allSlots[0];
      setTerritory({
        territoryId: firstSlot?.territoryId,
        time_zone: firstSlot?.territoryTimeZone,
      });
      const territoryTimezone = firstSlot?.territoryTimeZone || "America/Denver";
      setTimezone(territoryTimezone);
      setTimeSlots(allSlots);
    } catch (error) {
      console.error("Error fetching survey data:", error);
      if (isMountedRef.current) {
        setTimeSlots([]);
      }
    }
    if (isMountedRef.current) {
      setLoading(false);
    }
  };

  // Function to group time slots by day and consolidate by time
  const groupTimeSlotsByDay = (slots) => {
    const grouped = {};

    slots.forEach((slot) => {
      const { date, dayOfWeek } = slot;
      if (!grouped[date]) {
        grouped[date] = {
          day: date,
          dayOfWeek: dayOfWeek,
          time_slots: {},
        };
      }

      const timeKey = slot.startTime;
      if (!grouped[date].time_slots[timeKey]) {
        grouped[date].time_slots[timeKey] = {
          start_time: slot.startTime,
          end_time: slot.endTime,
          start_time_datetime: slot.startTimeDatetime,
          end_time_datetime: slot.endTimeDatetime,
          day: slot.date,
          day_of_week: slot.dayOfWeek,
          duration: slot.duration,
          available_surveyors: [],
        };
      }

      // Add surveyor info to this time slot
      grouped[date].time_slots[timeKey].available_surveyors.push({
        contact_id: slot.contactId,
        resource_id: slot.resourceId,
        territory_id: slot.territoryId,
      });
    });

    // Convert to array and sort by date, then convert time_slots to array
    return Object.values(grouped)
      .sort((a, b) => {
        return moment(a.day, "M/D/YY").isBefore(moment(b.day, "M/D/YY"))
          ? -1
          : 1;
      })
      .map((day) => ({
        ...day,
        time_slots: Object.values(day.time_slots).sort((a, b) => {
          return moment(a.start_time, "h:mm A").isBefore(
            moment(b.start_time, "h:mm A"),
          )
            ? -1
            : 1;
        }),
      }));
  };

  const isMountedRef = useRef(true);

  // Update formatted times when timezone or appointment changes
  useEffect(() => {
    if (appointment.startTimeDatetime && appointment.endTimeDatetime) {
      setFormatStartTime(
        moment(appointment.startTimeDatetime)
          .utc()
          .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
      );
      setFormatEndTime(
        moment(appointment.endTimeDatetime)
          .utc()
          .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
      );
    } else if (
      appointment.day &&
      appointment.startTime &&
      appointment.endTime &&
      timezone
    ) {
      // Convert time slots to UTC using the territory's timezone
      const newFormatStartTime = convertTimeSlotToUTC(
        appointment.day,
        appointment.startTime,
        timezone,
      );
      const newFormatEndTime = convertTimeSlotToUTC(
        appointment.day,
        appointment.endTime,
        timezone,
      );

      setFormatStartTime(newFormatStartTime);
      setFormatEndTime(newFormatEndTime);
    }
  }, [
    timezone,
    appointment.day,
    appointment.startTime,
    appointment.endTime,
    appointment.startTimeDatetime,
    appointment.endTimeDatetime,
  ]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [slotsVisible, setSlotsVisible] = useState(false);

  const findAppts = async () => {
    setAppointment(initialAppointment);
    setSlotsVisible(true);
    await getSurveyTimes(projectID, customerAddress, headers);
  };
  useEffect(() => {
    if (!slotsVisible) return;
    const intervalId = setInterval(() => {
      if (isMountedRef.current) {
        getSurveyTimes(projectID, customerAddress, headers);
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [slotsVisible]);

  useEffect(() => {
    getCustomerAppt();
    getCallPilot();
  }, []);

  const confirmAppointment = (appointment) => {
    setAppointment(appointment);
    setConfirmModal(true);
  };

  const handleConfirmAppointment = () => {
    createAppt();
  };

  const handleCancelAppointment = () => {
    setConfirmModal(false);
    setAppointment(initialAppointment);
  };

  return (
    <div>
      {(callComplete || contractSigned || envelopeCompleted) && (
        <>
          {currentAppt.id ? (
            <>
              <div className="col">
                <div className="row">
                  <div className="savingsText modals props">
                    {primaryContact.fullName}'s Solar Home Assessment{" "}
                  </div>
                  <div className="savingsText modals props">
                    {" "}
                    is scheduled for {currentAppt.startTime}.
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {!slotsVisible && (
                <button
                  className={"proposalButton confirm"}
                  disabled={loading}
                  onClick={() => {
                    findAppts();
                  }}
                >
                  {loading ? (
                    <CircularProgress size={40} color="inherit" />
                  ) : (
                    <>
                      <span>Find Available Site Survey Times</span>
                    </>
                  )}
                </button>
              )}
              {timeSlots && timeSlots.length > 0 && (
                <div style={{ marginTop: "20px" }}>
                  <h2 style={{ marginBottom: "20px" }}>
                    Available Time Slots:
                  </h2>
                  {groupTimeSlotsByDay(timeSlots).map((dayData) => (
                    <div key={dayData.day} style={{ marginBottom: "30px" }}>
                      <h3 style={{ marginBottom: "15px", color: "#333" }}>
                        {dayData.dayOfWeek}, {dayData.day}
                      </h3>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          flexWrap: "wrap",
                          gap: "10px",
                          marginBottom: "20px",
                        }}
                      >
                        {dayData.time_slots.map((timeSlot, slotIndex) => {
                          const availableCount =
                            timeSlot.available_surveyors.length;
                          const isAvailable = availableCount > 0;

                          return (
                            <div
                              key={slotIndex}
                              style={{
                                position: "relative",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                minWidth: "120px",
                                padding: "10px",
                                border: "1px solid #ddd",
                                borderRadius: "8px",
                                backgroundColor: "#f9f9f9",
                              }}
                            >
                              <button
                                className="custButton custLink"
                                style={{
                                  marginBottom: "5px",
                                  padding: "6px 10px",
                                  fontSize: "14px",
                                  whiteSpace: "nowrap",
                                  minWidth: "130px",
                                  opacity: isAvailable ? 1 : 0.5,
                                  cursor: isAvailable
                                    ? "pointer"
                                    : "not-allowed",
                                }}
                                disabled={!isAvailable}
                                onClick={() => {
                                  if (isAvailable) {
                                    // Automatically select the first available surveyor
                                    const firstSurveyor =
                                      timeSlot.available_surveyors[0];

                                    confirmAppointment({
                                      contactId: firstSurveyor.contact_id,
                                      resourceId: firstSurveyor.resource_id,
                                      territoryId: firstSurveyor.territory_id,
                                      day: timeSlot.day,
                                      dayOfWeek: timeSlot.day_of_week,
                                      startTime: timeSlot.start_time,
                                      endTime: timeSlot.end_time,
                                      startTimeDatetime:
                                        timeSlot.start_time_datetime,
                                      endTimeDatetime: timeSlot.end_time_datetime,
                                    });
                                  }
                                }}
                              >
                                {timeSlot.start_time}
                              </button>

                              {/* Availability count badge */}
                              <div
                                style={{
                                  position: "absolute",
                                  bottom: "5px",
                                  right: "5px",
                                  width: "20px",
                                  height: "20px",
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                  color: "white",
                                  backgroundColor: isAvailable
                                    ? "#28a745"
                                    : "#dc3545",
                                }}
                              >
                                {availableCount}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {timeSlots.length < 1 && slotsVisible && !loading ? (
                <>
                  <div className="savingsText modals props">
                    No appointments available through Proposalist.{" "}
                  </div>
                  <div className="savingsText modals props">
                    Please reach out to Sales Support to schedule
                  </div>
                  <div className="savingsText modals props">
                    {" "}
                    at <a href="tel: 4353346079">(435) 334-6079</a>.
                  </div>
                </>
              ) : (
                <>
                  {loading && slotsVisible ? (
                    <>
                      <div className="savingsText modals props">
                        Refreshing Appointments...{" "}
                        <CircularProgress size={25} color="inherit" />
                      </div>
                    </>
                  ) : null}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Confirmation Modal */}
      <Modal
        isOpen={confirmModal}
        onClose={handleCancelAppointment}
        title="Confirm Site Survey Appointment"
        showCloseButton={true}
        closeOnOverlayClick={true}
        className="medium"
      >
        {appointment && (
          <div style={{ textAlign: "center" }}>
            <div style={{ marginBottom: "24px" }}>
              <h3 style={{ marginBottom: "16px", color: "#333" }}>
                Site Survey Appointment Details
              </h3>
              <div
                style={{ fontSize: "16px", lineHeight: "1.6", color: "#666" }}
              >
                <p>
                  <strong>Date:</strong> {appointment.dayOfWeek},{" "}
                  {appointment.day}
                </p>
                <p>
                  <strong>Time:</strong> {appointment.startTime} -{" "}
                  {appointment.endTime}
                </p>
                <p>
                  <strong>Customer:</strong> {primaryContact.fullName}
                </p>
                <p>
                  <strong>Address:</strong> {customerAddress}
                </p>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "16px",
                marginTop: "24px",
              }}
            >
              <button
                className="proposalButton confirm"
                onClick={handleConfirmAppointment}
                disabled={loading}
                style={{
                  padding: "12px 24px",
                  fontSize: "16px",
                  fontWeight: "600",
                }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Confirm Appointment"
                )}
              </button>
              <button
                className="proposalButton"
                onClick={handleCancelAppointment}
                style={{
                  padding: "12px 24px",
                  fontSize: "16px",
                  fontWeight: "600",
                  backgroundColor: "#6c757d",
                  color: "white",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SiteSurvey;
